import { useCallback } from 'react';
import axios from 'axios';
import useWfApi from './axios/useWfApi';
import { WfPaginatedWorkplace, WfWorkplace } from '../models/WfWorkplace';
import { WfReservedOptions } from '../stores/WfFilterStore';
import { WfDataSummary } from '../models/WfDataSummary';
import { wfWorkplaceCSVRequestBody } from '../service/helper/wfWorkplaceCSVRequestBody';
import {
  WfBranchSummaryResponse,
  WfDisasterAreaBranchResponse,
  WfDisasterAreaSalesOfficeResponse,
  WfSalesOfficeSummaryResponse,
} from '../models/WfSummary';
import wfAxiosInstance from './axios/wfAxiosInstance';

export interface WfWorkplaceSearchParameters {
  reserved?: WfReservedOptions;
  branch?: string;
  salesOffice?: string;
  section?: string;
  squad?: string;
  statusList?: string[];
  customerNumber?: string;
  disasterId?: string;
}

export interface WfWorkplacePaginatedSearchParameters {
  searchParams?: WfWorkplaceSearchParameters;
  pageNumber: number;
  pageSize: number;
}

export interface WfWorkplaceReservationParameters {
  engineerUserId: string;
  reserverUserId: string;
  disasterId: string;
  branchCode: string;
  customerId: string;
}
export interface SummaryByBranchParameters {
  disasterId: string;
  branchCode?: string;
}

export interface SummaryBySalesOfficeParameters {
  disasterId: string;
  branchCode: string;
  salesOfficeCode: string;
}

function cleanParams<T extends object>(params: T): Partial<T> {
  return Object.fromEntries(
    Object.entries(params as Record<string, unknown>).filter(([, value]) => {
      if (value === undefined || value === null) return false;
      if (typeof value === 'string' && value === '  ') return true; // Allow Okinichi
      if (typeof value === 'string' && value.trim() === '') return false;
      if (Array.isArray(value) && value.length === 0) return false;
      return true;
    })
  ) as Partial<T>;
}

function useWfWorkplaceRepo() {
  const { get, post } = useWfApi();

  const postWorkplaceSearchParametersRequest = useCallback(
    async (parameters: WfWorkplaceSearchParameters) => {
      try {
        return (await post<WfWorkplace[]>('/api/wf/workplaces/search', parameters)) ?? [];
      } catch (error) {
        if (axios.isAxiosError(error) && error.code === 'ECONNABORTED') {
          throw error;
        }
        console.log(error);
        return [];
      }
    },
    [post]
  );

  const postWorkplacePaginatedParametersRequest = useCallback(
    async (parameters: WfWorkplacePaginatedSearchParameters) => {
      try {
        return (await post<WfPaginatedWorkplace>('/api/wf/workplaces/paginated', parameters)) ?? [];
      } catch (error) {
        if (axios.isAxiosError(error) && error.code === 'ECONNABORTED') {
          throw error;
        }
        console.log(error);
        return [];
      }
    },
    [post]
  );

  const postCreateWorkplaceReservationRequest = useCallback(
    async (body: WfWorkplaceReservationParameters) => {
      try {
        return await post<void>('/api/wf/workplaces/reservations/create', body);
      } catch (error) {
        console.log(error);
        return;
      }
    },
    [post]
  );

  const postCancelWorkplaceReservationRequest = useCallback(
    async (body: WfWorkplaceReservationParameters) => {
      try {
        return await post<void>('/api/wf/workplaces/reservations/cancel', body);
      } catch (error) {
        console.log(error);
        return;
      }
    },
    [post]
  );

  const getWorkplaceSummaryRequest = useCallback(async () => {
    try {
      return (await get<WfDataSummary>('/api/wf/workplaces/summary')) ?? null;
    } catch (error) {
      console.log(error);
      return null;
    }
  }, [get]);

  const getWfWorkplaceCSV = useCallback(async (body: wfWorkplaceCSVRequestBody): Promise<Blob> => {
    const response = await wfAxiosInstance.post<BlobPart>('api/wf/workplaces/csv/export', body, {
      responseType: 'blob',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'text/csv',
      },
    });

    if (response.data instanceof Blob) {
      return response.data;
    }

    return new Blob([response.data], { type: 'text/csv;charset=utf-8;' });
  }, []);

  const getSummaryByBranch = useCallback(
    async (parameters: SummaryByBranchParameters): Promise<WfBranchSummaryResponse[] | null> => {
      try {
        const response = await get<WfBranchSummaryResponse[]>('/api/wf/workplaces/summary/branch', {
          params: cleanParams(parameters),
        });

        if (Array.isArray(response)) {
          return response;
        }

        return null;
      } catch (error) {
        console.error('REPO ERROR:', error);
        return null;
      }
    },
    [get]
  );

  const getDisasterAreaSummaryByBranch = useCallback(
    async (disasterId: string): Promise<WfDisasterAreaBranchResponse[] | null> => {
      try {
        const response = await get<WfDisasterAreaBranchResponse[]>(
          '/api/wf/summary/disaster-area/branch',
          {
            params: { disasterId },
          }
        );

        if (response && Array.isArray(response)) {
          return response;
        }

        return null;
      } catch (error) {
        console.error('Error fetching disaster area summary by branch:', error);
        return null;
      }
    },
    [get]
  );

  const getSummaryBySalesOffice = useCallback(
    async (
      parameters: SummaryBySalesOfficeParameters
    ): Promise<WfSalesOfficeSummaryResponse[] | null> => {
      try {
        const response = await get<WfSalesOfficeSummaryResponse[]>(
          '/api/wf/workplaces/summary/sales-office',
          {
            params: cleanParams(parameters),
          }
        );

        if (response && Array.isArray(response)) {
          return response;
        }

        return null;
      } catch (error) {
        console.error('Error fetching sales office summary:', error);
        return null;
      }
    },
    [get]
  );

  const getDisasterAreaSummaryBySalesOffice = useCallback(
    async (
      disasterId: string,
      branchCode: string
    ): Promise<WfDisasterAreaSalesOfficeResponse | null> => {
      try {
        const response = await get<string[]>('/api/wf/summary/disaster-area/sales-office', {
          params: { disasterId, branchCode },
        });

        if (Array.isArray(response)) {
          return response;
        }

        return null;
      } catch (error) {
        console.error('Error fetching disaster area sales offices:', error);
        return null;
      }
    },
    [get]
  );

  return {
    postWorkplaceSearchParametersRequest,
    postWorkplacePaginatedParametersRequest,
    postCreateWorkplaceReservationRequest,
    postCancelWorkplaceReservationRequest,
    getWorkplaceSummaryRequest,
    getWfWorkplaceCSV,
    getSummaryBySalesOffice,
    getSummaryByBranch,
    getDisasterAreaSummaryByBranch,
    getDisasterAreaSummaryBySalesOffice,
  };
}

export default useWfWorkplaceRepo;
